// Ported from src/contexts/CartContext.tsx — the cart lives entirely in
// localStorage (no server round-trip), synced across tabs via the `storage`
// event. Every mutation persists then dispatches a `cart:changed` window
// event so any Stimulus controller can re-render its own bit of UI.
const STORAGE_KEY = "bookmania.cart.v1"

function isCartItem(v) {
  return (
    v && typeof v === "object" &&
    typeof v.title === "string" &&
    typeof v.author === "string" &&
    typeof v.price === "number" &&
    typeof v.condition === "string" &&
    typeof v.quantity === "number" &&
    typeof v.stock === "number"
  )
}

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(isCartItem)
      .map((item) => ({ ...item, id: String(item.id), image_url: item.image_url ?? null, genreIds: item.genreIds ?? [] }))
  } catch {
    return []
  }
}

function writeStored(cart) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cart))
  } catch {
    // localStorage indisponível (modo privado do Safari, quota) — ignora
  }
}

let cart = readStored()

function notify() {
  window.dispatchEvent(new CustomEvent("cart:changed", { detail: { cart } }))
}

function persistAndNotify() {
  writeStored(cart)
  notify()
}

window.addEventListener("storage", (event) => {
  if (event.key !== STORAGE_KEY) return
  cart = readStored()
  notify()
})

export function getCart() {
  return cart
}

export function totalCount() {
  return cart.reduce((sum, item) => sum + item.quantity, 0)
}

export function findItem(id) {
  return cart.find((item) => item.id === String(id))
}

export function addToCart(book) {
  book = { ...book, id: String(book.id) }
  const existing = cart.find((item) => item.id === book.id)
  if (existing) {
    cart = cart.map((item) => item.id === book.id
      ? { ...item, ...book, price: Number(book.price), quantity: Math.min(item.quantity + 1, book.stock) }
      : item).filter(item => item.quantity > 0)
    persistAndNotify()
    return
  }

  if (book.stock <= 0) return

  cart = [
    ...cart,
    {
      id: book.id,
      title: book.title,
      author: book.author,
      price: typeof book.price === "number" ? book.price : parseFloat(book.price),
      internal_code: book.internal_code ?? null,
      condition: book.condition,
      image_url: book.image_url || null,
      quantity: 1,
      stock: book.stock,
      genreIds: book.genreIds ?? [],
    },
  ]
  persistAndNotify()
}

export function removeFromCart(id) {
  cart = cart.filter((item) => item.id !== String(id))
  persistAndNotify()
}

export function incrementItem(id) {
  cart = cart.map((item) => (item.id === String(id) && item.quantity < item.stock ? { ...item, quantity: item.quantity + 1 } : item))
  persistAndNotify()
}

export function decrementItem(id) {
  cart = cart
    .map((item) => (item.id === String(id) ? { ...item, quantity: item.quantity - 1 } : item))
    .filter((item) => item.quantity > 0)
  persistAndNotify()
}

export function clearCart() {
  cart = []
  persistAndNotify()
}

export function replaceCart(items) {
  cart = items
  persistAndNotify()
}
